import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { LayoutGrid, List, SearchX, CalendarX2, FilterX } from 'lucide-react';
import { useEncounterFeed } from '../../hooks/useEncounters';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import {
  DateRangeFilter, Avatar, EmptyState, PageHeader,
  SearchInput, Select, Button, SkeletonRow,
} from '@parsina/framework';
import { EncounterCardSkeleton } from './EncountersSkeleton';
import StatusChip from '@parsina/framework/clinical/src/components/common/StatusChip';
import SegmentedControl from '../../components/common/SegmentedControl';
import InfiniteSentinel, { canObserve } from '../../components/common/InfiniteSentinel';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import { VisitNowButton } from '@parsina/framework/clinical';
// سوابق ویزیت lists PEOPLE, newest first, and loads as the doctor scrolls (PM-0533) —
// the owner's call, a named exception to «pagination over infinite scroll» (DESIGN
// SYSTEM §15). The phone keeps its «نمایش بیشتر» expander.
//
// The vocabularies, the column definition and the URL-is-state contract are
// shared with the phone surface (§1.1). They were extracted from THIS file
// unchanged when Encounters.mobile.jsx was built, so the desktop behaviour is
// the reference and neither surface holds a second opinion.
import {
  ENC_STATUS, FILTERS, COLUMNS, PAGE_SIZE,
  colLabel, cellValue, datePresetsFor,
  useEncounterFilters, useClinicOptions, useEncounterSearch,
} from './Encounters.shared';

// Card entrance only. Shimmer comes from the framework Skeleton* primitives (§2.4);
// reduced-motion collapses the entrance to instant (§12.4).
const KF = `
  @keyframes encFadeUp { from { opacity:0; transform:translateY(0.5rem) } to { opacity:1; transform:translateY(0) } }
  .enc-fade { animation: encFadeUp .25s ease }
  @media (prefers-reduced-motion: reduce) { .enc-fade { animation: none } }
`;

function EncStatus({ status }) {
  const { t } = useTranslation();
  const m = ENC_STATUS[status] || { key: status, label: undefined };
  return <StatusChip status={m.key} label={m.labelKey ? t(m.labelKey) : undefined} />;
}

// «ویزیت همین الان» on every person in the list (PM-0566) — the same action as the
// record's header: queue the patient today and open the visit room. The row's clinic
// (where they were last seen) is the button's preferred clinic. One shared read of
// the doctor's clinics serves every row.
const visitNowPatient = (e) => ({ id: e.patientId, encounters: e.clinicId ? [{ clinicId: e.clinicId }] : [] });

// The row is a link to the record: a press on its button must not ALSO open it.
const stopRow = (ev) => ev.stopPropagation();

const VIEWS = [
  { id: 'card', icon: <LayoutGrid size={16} aria-hidden="true" />, ariaLabelKey: 'encounters.view.card' },
  { id: 'list', icon: <List size={16} aria-hidden="true" />,       ariaLabelKey: 'encounters.view.list' },
];


// ── EncounterCard ────────────────────────────────────────────────────────────
function EncounterCard({ e, clinicName, navigate }) {
  const i18n = useTranslation();
  const { t } = i18n;
  const goToProfile = () => navigate('patient-detail', { patientId: e.patientId, id: e.patientId });
  return (
    <article
      data-testid={`encounters-card-${e.id}`}
      className="enc-fade bg-white rounded-2xl border-[1.5px] border-border p-5 flex flex-col gap-3.5 shadow-card transition-[border-color,box-shadow] duration-200 hover:border-navy hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
    >
      {/* Top row */}
      <div className="flex items-start gap-3">
        <Avatar name={e.patientName || ''} size={48} />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-foreground mb-1 break-words">
            {e.patientName || t('encounters.unknownPatient')}
          </div>
          <div className="text-xs text-muted-foreground">
            {cellValue('type', e, null, i18n)}
          </div>
        </div>
        <EncStatus status={e.status} />
      </div>

      {/* Divider */}
      <div className="h-px bg-border" />

      {/* Meta — labels come from the shared column definition */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-background rounded-lg px-2.5 py-1.5">
          <div className="text-[0.625rem] text-muted-foreground mb-0.5">{colLabel(t, 'date')}</div>
          <div className="text-xs font-semibold text-foreground">{cellValue('date', e, null, i18n)}</div>
        </div>
        {clinicName && (
          <div className="bg-background rounded-lg px-2.5 py-1.5 min-w-0">
            <div className="text-[0.625rem] text-muted-foreground mb-0.5">{colLabel(t, 'clinic')}</div>
            <div className="text-xs font-semibold text-foreground truncate" title={clinicName}>{clinicName}</div>
          </div>
        )}
        <div className="bg-background rounded-lg px-2.5 py-1.5">
          <div className="text-[0.625rem] text-muted-foreground mb-0.5">{colLabel(t, 'fileNumber')}</div>
          <div className={`text-xs font-semibold tabular-nums ${e.fileNumber ? 'text-foreground' : 'text-muted-foreground'}`}>
            {cellValue('fileNumber', e, null, i18n)}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2">
        {e.patientId && (
          <VisitNowButton patient={visitNowPatient(e)} fullWidth compact testId={`encounters-card-${e.id}-visit-now`} />
        )}
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={goToProfile}
          disabled={!e.patientId}
          data-testid={`encounters-card-${e.id}-profile`}
        >
          {t('encounters.viewProfile')}
        </Button>
      </div>
    </article>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

export default function EncountersWeb() {
  const i18n = useTranslation();
  const { t, formatNumber } = i18n;
  const navigate = useAppNavigate();

  const {
    setParams, filter, type, clinicId, query, preset, custom,
    fromTs, toTs, hasActiveFilters, clearFilters, view: rawView,
  } = useEncounterFilters();
  const view = VIEWS.some(v => v.id === rawView) ? rawView : 'card';

  const { clinics, clinicNameById } = useClinicOptions();
  const { search, onSearch: handleSearch } = useEncounterSearch(query, setParams);

  const {
    encounters: all, total, loading, loadingMore, error, moreError, hasMore, loadMore, reload,
  } = useEncounterFeed(
    filter !== 'all' ? filter : null, query, clinicId, type,
    { fromTs, toTs, pageSize: PAGE_SIZE, onePerPatient: true },
  );

  const isEmpty   = !loading && !error && all.length === 0;
  const dataState = loading ? 'loading' : error ? 'error' : isEmpty ? 'empty' : 'content';
  // Stale-while-refetch (§2.4/§8): on a refetch keep the previous page visible,
  // dimmed to 45%, instead of blanking to skeletons. Skeletons are first-load only.
  const stale     = loading && all.length > 0;
  const skeleton  = loading && all.length === 0;

  const clinicOptions = clinics.map(c => ({ value: c.id, label: c.name }));

  // Filtered-empty vs truly-empty (§2.4): a filtered-out list never claims the
  // dataset is empty. The default view is date-scoped to «این ماه», so the unfiltered
  // empty copy makes no claim about a particular day.
  const emptyState = hasActiveFilters ? (
    <EmptyState
      icon={<SearchX size={56} className="text-muted-foreground" aria-hidden="true" />}
      title={t('errors.empty.filters')}
      description={t('encounters.empty.filtersHint')}
      action={
        <Button variant="outline" size="sm" onClick={clearFilters} data-testid="encounters-empty-clear">
          <FilterX size={15} aria-hidden="true" />
          {t('encounters.clearFilters')}
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={<CalendarX2 size={56} className="text-muted-foreground" aria-hidden="true" />}
      title={t('visit.empty.noVisits')}
      description={t('encounters.empty.todayHint')}
    />
  );

  return (
    <div className="min-h-full flex flex-col bg-background">
      <style>{KF}</style>

      <PageHeader title={t('encounters.title')} sub={t('encounters.subtitle')} />

      {/* ── Toolbar — zone ④: one white card row (§2.2, §9.2) ── */}
      <div className="px-8 pt-4">
        <div
          data-testid="encounters-toolbar"
          className="bg-white border border-border rounded-2xl shadow-card px-4 py-3 flex flex-wrap items-center gap-3"
        >
          <div className="flex-1 min-w-[12.5rem] max-w-[25rem]" data-testid="encounters-toolbar-search">
            <SearchInput
              value={search}
              onChange={e => handleSearch(e.target.value)}
              placeholder={t('encounters.searchPlaceholder')}
              clearLabel={t('common.actions.clearSearch')}
            />
          </div>

          {/* زمان (time) filter — default این ماه */}
          <div data-testid="encounters-toolbar-daterange">
            <DateRangeFilter
              presets={datePresetsFor(t)}
              preset={preset}
              custom={custom}
              onPreset={(id) => setParams({ preset: id, from: '', to: '' })}
              onCustom={(from, to) => setParams({ preset: 'custom', from, to })}
            />
          </div>

          {/* Clinic filter */}
          {clinics.length > 0 && (
            <div className="min-w-[10rem]" data-testid="encounters-toolbar-clinic">
              <Select
                value={clinicId}
                onChange={e => setParams({ clinic: e.target.value })}
                options={clinicOptions}
                placeholder={t('encounters.allClinics')}
              />
            </div>
          )}

          {/* Type filter */}
          <div className="min-w-[8.5rem]" data-testid="encounters-toolbar-type">
            <Select
              value={type}
              onChange={e => setParams({ type: e.target.value })}
              options={[
                { value: 'in-person', label: t('encounters.type.inPerson') },
                { value: 'online', label: t('encounters.type.online') },
              ]}
              placeholder={t('encounters.allTypes')}
            />
          </div>

          {/* Status filter pills */}
          <SegmentedControl items={FILTERS.map(f => ({ ...f, label: t(f.labelKey) }))} value={filter} onChange={id => setParams({ status: id })} ariaLabel={t('encounters.statusFilter')} testId="encounters-toolbar-status" />

          {/* View toggle */}
          <SegmentedControl items={VIEWS.map(v => ({ ...v, ariaLabel: t(v.ariaLabelKey) }))} value={view} onChange={id => setParams({ view: id })} ariaLabel={t('encounters.viewMode')} testId="encounters-toolbar-view" />

          {/* Clear filters — visible only when ≥1 filter is active (§9.2) */}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="encounters-toolbar-clear">
              <FilterX size={15} aria-hidden="true" />
              {t('encounters.clearFilters')}
            </Button>
          )}

          <div className="text-xs text-muted-foreground whitespace-nowrap ms-auto" data-testid="encounters-toolbar-count">
            {loading ? '…' : t('patient.lookup.count', { count: total })}
          </div>
        </div>
      </div>

      {/* ── Content — zone ⑤, P3 collection (card grid ⇄ table) ── */}
      <div data-testid="encounters-content" data-state={dataState} className="px-8 pb-8 pt-4" aria-busy={loading || undefined}>

        {/* A failed query must not look like an empty result. */}
        {error ? (
          <div data-testid="encounters-error">
            <ErrorState
              error={error}
              title={t('encounters.unavailable')}
              onRetry={reload}
            />
          </div>
        ) : (
        <>

        {/* Card grid */}
        {view === 'card' && (
          isEmpty ? emptyState : (
            <div className={`grid grid-cols-[repeat(auto-fill,minmax(16.25rem,1fr))] gap-4 transition-opacity ${stale ? 'opacity-[0.45] pointer-events-none' : ''}`}>
              {skeleton
                ? Array.from({ length: PAGE_SIZE }).map((_, i) => <EncounterCardSkeleton key={i} i={i} />)
                : all.map(e => <EncounterCard key={e.id} e={e} clinicName={clinicNameById[e.clinicId]} navigate={navigate} />)
              }
              {loadingMore && Array.from({ length: 3 }).map((_, i) => <EncounterCardSkeleton key={`more-${i}`} i={i} />)}
            </div>
          )
        )}

        {/* List view — empty/error render inside the card where rows would be (§5.1) */}
        {/* max-[1180px] is the sanctioned Compact threshold (§4.1): below it the
            table scrolls inside its own container (§4.2) — sticky header degrades
            there but the page body never scrolls horizontally. */}
        {view === 'list' && (
          <div className={`bg-white rounded-2xl border border-border max-[1180px]:overflow-x-auto transition-opacity ${stale ? 'opacity-[0.45] pointer-events-none' : ''}`}>
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {COLUMNS.map(c => (
                    <th
                      key={c.id}
                      className="sticky top-0 z-10 bg-background px-5 py-3.5 text-xs text-muted-foreground font-bold whitespace-nowrap text-start border-b border-border first:rounded-ss-2xl last:rounded-se-2xl"
                    >
                      {t(c.labelKey)}
                    </th>
                  ))}
                  <th className="sticky top-0 z-10 bg-background px-5 py-3.5 border-b border-border last:rounded-se-2xl">
                    <span className="sr-only">{t('patient.visitNow.action')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {skeleton
                  ? Array.from({ length: PAGE_SIZE }).map((_, i) => <SkeletonRow key={i} cols={COLUMNS.length + 1} />)
                  : all.map(e => (
                    <tr
                      key={e.id}
                      data-testid={`encounters-row-${e.id}`}
                      role="link"
                      tabIndex={e.patientId ? 0 : undefined}
                      aria-label={t('encounters.recordOf', {
                        name: e.patientName || t('encounters.unknownPatient'),
                      })}
                      onClick={() => e.patientId && navigate('patient-detail', { patientId: e.patientId, id: e.patientId })}
                      onKeyDown={ev => { if (ev.key === 'Enter' && e.patientId) navigate('patient-detail', { patientId: e.patientId, id: e.patientId }); }}
                      className={`border-t border-border transition-colors ${e.patientId ? 'cursor-pointer hover:bg-muted/50 focus-visible:bg-muted/50 outline-none' : ''}`}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <Avatar name={e.patientName || ''} size={36} />
                          <span className="font-bold text-sm text-foreground whitespace-nowrap max-w-[14rem] truncate" title={e.patientName || undefined}>
                            {e.patientName || t('encounters.unknownPatient')}
                          </span>
                        </div>
                      </td>
                      <td className={`px-5 py-3 text-[0.8125rem] whitespace-nowrap tabular-nums ${e.fileNumber ? 'text-foreground' : 'text-muted-foreground'}`}>{cellValue('fileNumber', e, null, i18n)}</td>
                      <td className="px-5 py-3 text-[0.8125rem] text-foreground/80 whitespace-nowrap">{cellValue('type', e, null, i18n)}</td>
                      <td className="px-5 py-3 text-[0.8125rem] text-foreground/80 whitespace-nowrap">{cellValue('date', e, null, i18n)}</td>
                      <td className="px-5 py-3 text-[0.8125rem] text-foreground/80 whitespace-nowrap max-w-[12rem] truncate" title={clinicNameById[e.clinicId] || undefined}>{cellValue('clinic', e, clinicNameById[e.clinicId], i18n)}</td>
                      <td className="px-5 py-3"><EncStatus status={e.status} /></td>
                      <td className="px-5 py-3 whitespace-nowrap" onClick={stopRow} onKeyDown={stopRow}>
                        {e.patientId && (
                          <VisitNowButton patient={visitNowPatient(e)} compact testId={`encounters-row-${e.id}-visit-now`} />
                        )}
                      </td>
                    </tr>
                  ))
                }
                {loadingMore && Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={`more-${i}`} cols={COLUMNS.length + 1} />)}
              </tbody>
            </table>
            {isEmpty && emptyState}
          </div>
        )}

        {/* Infinite scroll — zone ⑥. The sentinel asks for the next rows a screenful
            before the doctor reaches the end; the button is for a browser with no
            IntersectionObserver, and the retry is for a page that failed to load (the
            rows already on screen stay). */}
        {!skeleton && total > 0 && (
          <div data-testid="encounters-more-region" className="pt-4">
            <InfiniteSentinel active={hasMore} onVisible={loadMore} watch={all.length} testId="encounters-sentinel" />
            {moreError ? (
              <div data-testid="encounters-more-error">
                <ErrorState error={moreError} title={t('encounters.unavailable')} onRetry={loadMore} />
              </div>
            ) : (hasMore && !canObserve() && (
              <Button variant="outline" className="w-full" onClick={loadMore} data-testid="encounters-more">
                {t('dashboard.queue.showMore', { count: formatNumber(total - all.length) })}
              </Button>
            ))}
          </div>
        )}

        </>
        )}
      </div>
    </div>
  );
}
